import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const DraftboardPreview = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // Mock data for preview
  const mockPlayers = [
    { name: 'Christian McCaffrey', position: 'RB', team: 'SF', tier: 1 },
    { name: 'Josh Allen', position: 'QB', team: 'BUF', tier: 1 },
    { name: 'Cooper Kupp', position: 'WR', team: 'LAR', tier: 1 },
    { name: 'Derrick Henry', position: 'RB', team: 'BAL', tier: 1 },
    { name: 'Tyreek Hill', position: 'WR', team: 'MIA', tier: 1 },
    { name: 'Travis Kelce', position: 'TE', team: 'KC', tier: 1 },
  ];

  const positionColors = {
    QB: colors.blueAccent[600],
    RB: colors.greenAccent[600],
    WR: colors.yellowAccent[600],
    TE: colors.redAccent[600],
  };

  const handleTryDraftboard = () => {
    navigate('/draftboard');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: colors.primary[400],
        border: `1px solid ${colors.grey[700]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${alpha(colors.blueAccent[700], 0.8)}, ${alpha(colors.blueAccent[500], 0.6)})`,
          borderBottom: `1px solid ${colors.grey[700]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: alpha(colors.grey[100], 0.1),
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GridViewIcon sx={{ fontSize: 28, color: colors.grey[100] }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              Interactive Draftboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(colors.grey[100], 0.8),
                fontSize: '1rem',
              }}
            >
              Grid-based draft visualization with live tracking
            </Typography>
          </Box>
        </Box>

        {/* Feature highlights */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
            label="Live Draft Sync"
            size="small"
            sx={{
              backgroundColor: alpha(colors.greenAccent[500], 0.2),
              color: colors.greenAccent[100],
              border: `1px solid ${alpha(colors.greenAccent[500], 0.3)}`,
            }}
          />
          <Chip
            icon={<StarIcon sx={{ fontSize: 16 }} />}
            label="Player Rankings"
            size="small"
            sx={{
              backgroundColor: alpha(colors.yellowAccent[500], 0.2),
              color: colors.yellowAccent[100],
              border: `1px solid ${alpha(colors.yellowAccent[500], 0.3)}`,
            }}
          />
          <Chip
            icon={<PersonIcon sx={{ fontSize: 16 }} />}
            label="Keeper Management"
            size="small"
            sx={{
              backgroundColor: alpha(colors.redAccent[500], 0.2),
              color: colors.redAccent[100],
              border: `1px solid ${alpha(colors.redAccent[500], 0.3)}`,
            }}
          />
        </Box>
      </Box>

      {/* Preview Grid */}
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: colors.grey[100],
            fontWeight: 600,
            mb: 2,
          }}
        >
          Preview: Top Players Grid
        </Typography>

        <Grid container spacing={1} sx={{ mb: 3 }}>
          {mockPlayers.map((player, index) => (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: colors.primary[500],
                  border: `1px solid ${colors.grey[600]}`,
                  borderRadius: 1,
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    backgroundColor: colors.primary[450],
                    borderColor: positionColors[player.position],
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: positionColors[player.position],
                    color: colors.primary[900],
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                >
                  {player.position}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 600,
                    display: 'block',
                    fontSize: '10px',
                    lineHeight: 1.2,
                  }}
                >
                  {player.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[400],
                    fontSize: '9px',
                  }}
                >
                  {player.team}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Action Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleTryDraftboard}
            startIcon={<GridViewIcon />}
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.primary[900],
              fontWeight: 700,
              textTransform: 'uppercase',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: colors.blueAccent[500],
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Try Interactive Draftboard
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default DraftboardPreview;
